import TagManager from 'react-gtm-module';
import { measureOpenAIConversion } from './openai-pixel';

export * from './openai-pixel';

export const sendGAEvent = (
  category: string,
  action: string,
  label: string,
  value: string,
) => {
  const args = {
    dataLayer: {
      event: 'customEvent',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    },
  };

  TagManager.dataLayer(args);
};

export const sendTemplateGAEvent = (id: number, type: 'self' | 'ai') => {
  const eventAction =
    type === 'self' ? 'Template applied self guided' : 'Template applied ai';
  switch (id) {
    case 4:
      return sendGAEvent(
        'Template',
        eventAction,
        'lucky draw: fan page interaction - template applied',
        '',
      );
    case 5:
      return sendGAEvent(
        'Template',
        eventAction,
        'fun interactive quiz - template applied',
        '',
      );
    case 6:
      return sendGAEvent(
        'Template',
        eventAction,
        'event participation reminder - template applied',
        '',
      );
    case 7:
      return sendGAEvent(
        'Template',
        eventAction,
        'data (whitepaper) request - template applied',
        '',
      );
    case 8:
      return sendGAEvent(
        'Template',
        eventAction,
        'interactive event registration - template applied',
        '',
      );
    case 9:
      return sendGAEvent(
        'Template',
        eventAction,
        'guide customers to provide information - template applied',
        '',
      );
    case 10:
      return sendGAEvent(
        'Template',
        eventAction,
        'join membership for discounts - template applied',
        '',
      );
    case 11:
      return sendGAEvent(
        'Template',
        eventAction,
        'newsletter subscription promotion - template applied',
        '',
      );
    case 12:
      return sendGAEvent(
        'Template',
        eventAction,
        'restaurant menu and returning customer - template applied',
        '',
      );
    case 13:
      return sendGAEvent(
        'Template',
        eventAction,
        'returning customer discount promotion - template applied',
        '',
      );
    case 2:
      return sendGAEvent(
        'Template',
        eventAction,
        'automated faq response - template applied',
        '',
      );
    case 3:
      return sendGAEvent(
        'Template',
        eventAction,
        'lucky draw - template applied',
        '',
      );
    case 14:
      return sendGAEvent(
        'Template',
        eventAction,
        'psychological test - template applied',
        '',
      );
    case 15:
      return sendGAEvent(
        'Template',
        eventAction,
        'handoff to customer service - template applied',
        '',
      );
    case 16:
      return sendGAEvent(
        'Template',
        eventAction,
        'get subscribers by displaying your content - template applied',
        '',
      );
    case 17:
      return sendGAEvent(
        'Template',
        eventAction,
        'get subscribers and collect subscriber info - template applied',
        '',
      );
    default:
      return null;
  }
};

/**
 * 轉換事件（conversion events）
 *
 * WHY: 廣告平台（OpenAI Ads pixel、之後的 Meta / Google Ads 等）要在「註冊完成」「付款完成」
 * 這類關鍵行為上回報轉換。這裡只負責把事件以獨立的 event 名稱推進 GTM dataLayer，
 * 真正呼叫各家 pixel 的 tag 設在 GTM 後台用 Custom Event trigger 綁定，
 * 所以之後新增 / 停用廣告平台不必再改前端、不必重新部署，SIT / PREL 也不會送假轉換。
 *
 * 事件名稱固定成 union type，避免各頁各自手打字串而對不上 GTM trigger。
 */
export type ConversionEventName =
  | 'registration_completed' // 微型問卷送出成功：Email / FB 註冊兩路統一、Email 已過驗證
  | 'email_verified' // Email 註冊驗證信連結點擊成功
  | 'channel_connected' // 首次連接 FB 粉專 / IG / Threads 成功（啟用訊號）
  | 'begin_checkout' // 進入升級（結帳）頁
  | 'order_created' // 新訂閱付款成功（TapPay 3D 驗證回來、後端確認 status=1）
  | 'purchase_failed'; // 付款失敗（TapPay 回來後端回非成功）

/**
 * 所有轉換事件可能帶的參數鍵。
 * WHY: GTM 的資料層模型是累加的，上一個事件推的 channel / reason 會留在模型裡，
 * 下一個事件若 tag 有讀同名變數就會拿到舊值。所以每次送事件都把沒帶的鍵明確設回 undefined 清掉。
 */
const CONVERSION_PARAM_KEYS = [
  'channel',
  'social_type',
  'payment_id',
  'payment_type',
  'reason',
] as const;

export type ConversionParamKey = (typeof CONVERSION_PARAM_KEYS)[number];

export type ConversionEventParams = Partial<
  Record<ConversionParamKey, string | number | boolean | undefined>
>;

export const sendConversionEvent = (
  event: ConversionEventName,
  params: ConversionEventParams = {},
) => {
  const resetParams: ConversionEventParams = {};
  for (const key of CONVERSION_PARAM_KEYS) {
    resetParams[key] = undefined;
  }
  TagManager.dataLayer({ dataLayer: { event, ...resetParams, ...params } });
  // OpenAI Ads 只收兩個事件，由程式直接呼叫 pixel（不經 GTM）；未初始化（SIT/PREL）時 no-op
  measureOpenAIConversion(event);
};

const CONVERSION_ONCE_PREFIX = 'conversion_sent_';

/**
 * 同一個 key 在本瀏覽器分頁只送一次。
 * WHY: 付款結果頁重新整理、或 React effect 重跑時，order_created 不能重複回報，
 * 否則廣告平台的轉換數會灌水。用 sessionStorage 而不用 ref，是因為整頁 reload 會把 ref 清掉。
 * sessionStorage 不可用（隱私模式、被 block）時退回直接送出，寧可偶爾重複也不要漏報。
 */
export const sendConversionEventOnce = (
  key: string,
  event: ConversionEventName,
  params: ConversionEventParams = {},
) => {
  const storageKey = `${CONVERSION_ONCE_PREFIX}${event}_${key}`;
  try {
    if (window.sessionStorage.getItem(storageKey)) return false;
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    // sessionStorage 不可用：照送
  }
  sendConversionEvent(event, params);
  return true;
};
